"use strict";

/**
 * Client-side collection-membership filtering.
 *
 * Shopify cannot read custom query params (e.g. filter.age) in Liquid, and
 * "belongs to collection X" is not a native storefront filter. So Age and
 * Category filters are applied here in the browser.
 *
 * Fetches the complete server-sorted result set, keeps that order within each
 * availability group, then paginates in the browser. This guarantees that
 * unavailable products follow every available product across page boundaries.
 */
(function () {
  var GROUPS = ["age", "category"];
  var NAV_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="6" height="12" viewBox="0 0 6 12" fill="none">' +
    '<path d="M0.750015 10.6667L5.00002 5.70841L0.750016 0.75008" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    "</svg>";

  function getSelected() {
    var params = new URLSearchParams(window.location.search);
    var selected = {};

    GROUPS.forEach(function (group) {
      var values = [];
      ["filter." + group, "filter.p." + group].forEach(function (name) {
        params.getAll(name).forEach(function (value) {
          var trimmed = (value || "").trim();
          if (trimmed && values.indexOf(trimmed) === -1) {
            values.push(trimmed);
          }
        });
      });
      selected[group] = values;
    });

    return selected;
  }

  function hasSelection(selected) {
    return GROUPS.some(function (group) {
      return selected[group].length > 0;
    });
  }

  function cardMatches(card, selected) {
    var raw = " " + (card.getAttribute("data-product-collections") || "") + " ";

    return GROUPS.every(function (group) {
      var handles = selected[group];
      if (!handles.length) return true;
      return handles.some(function (handle) {
        return raw.indexOf(" " + handle + " ") !== -1;
      });
    });
  }

  function getPageSize(root) {
    var size = parseInt(root.getAttribute("data-page-size"), 10);
    if (!size || size > 50) size = 20;
    if (size < 1) size = 20;
    return size;
  }

  function sortAvailableFirst(cards) {
    return cards
      .map(function (card, index) {
        return { card: card, index: index };
      })
      .sort(function (a, b) {
        var aAvailable = a.card.getAttribute("data-product-available") === "true";
        var bAvailable = b.card.getAttribute("data-product-available") === "true";
        if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;
        return a.index - b.index;
      })
      .map(function (item) {
        return item.card;
      });
  }

  function hasUnavailableCards(root) {
    return Boolean(
      root.querySelector('[data-filter-grid] [data-product-available="false"]')
    );
  }

  function reorderCurrentGrid(root) {
    var grid = root.querySelector("[data-filter-grid]");
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll("[data-product-card]"));
    var ordered = sortAvailableFirst(cards);
    grid.replaceChildren.apply(grid, ordered);
  }

  function getPageFromUrl() {
    var page = parseInt(new URLSearchParams(window.location.search).get("page"), 10);
    return page > 0 ? page : 1;
  }

  function buildPageUrl(page) {
    var params = new URLSearchParams(window.location.search);
    if (page <= 1) {
      params.delete("page");
    } else {
      params.set("page", String(page));
    }
    var query = params.toString();
    return window.location.pathname + (query ? "?" + query : "") + window.location.hash;
  }

  function buildFetchUrl(page) {
    var params = new URLSearchParams(window.location.search);
    GROUPS.forEach(function (group) {
      params.delete("filter." + group);
      params.delete("filter.p." + group);
    });
    params.delete("page");
    if (page > 1) {
      params.set("page", String(page));
    }
    var query = params.toString();
    return window.location.pathname + (query ? "?" + query : "");
  }

  function buildAvailabilityFetchUrl(available, page) {
    var params = new URLSearchParams(window.location.search);
    GROUPS.forEach(function (group) {
      params.delete("filter." + group);
      params.delete("filter.p." + group);
    });
    params.delete("page");
    params.set("filter.v.availability", available ? "1" : "0");
    if (page > 1) params.set("page", String(page));
    var query = params.toString();
    return window.location.pathname + (query ? "?" + query : "");
  }

  async function fetchAvailabilityPage(root, available, page) {
    var response = await fetch(buildAvailabilityFetchUrl(available, page));
    if (!response.ok) throw new Error("Unable to load availability group");

    var html = await response.text();
    var doc = new DOMParser().parseFromString(html, "text/html");
    var remoteRoot =
      (root.id && doc.getElementById(root.id)) || doc.querySelector("[data-collection-filter]");
    if (!remoteRoot) throw new Error("Availability results missing");

    var remoteGrid = remoteRoot.querySelector("[data-filter-grid]");
    return {
      total: parseInt(remoteRoot.getAttribute("data-total-items"), 10) || 0,
      cards: remoteGrid
        ? Array.prototype.slice.call(remoteGrid.querySelectorAll("[data-product-card]"))
        : [],
    };
  }

  function pagesForRange(start, end, pageSize) {
    var pages = [];
    if (end <= start) return pages;
    var first = Math.floor(start / pageSize) + 1;
    var last = Math.floor((end - 1) / pageSize) + 1;
    for (var page = first; page <= last; page++) pages.push(page);
    return pages;
  }

  function cardAt(groupPages, index, pageSize) {
    var page = Math.floor(index / pageSize) + 1;
    var offset = index % pageSize;
    return groupPages[page] && groupPages[page].cards[offset];
  }

  function showLoader(root) {
    var loader = root.querySelector("[data-filter-loader]");
    if (!loader) return;
    loader.hidden = false;
    loader.setAttribute("aria-hidden", "false");
  }

  function hideLoader(root) {
    var loader = root.querySelector("[data-filter-loader]");
    if (!loader) return;
    loader.hidden = true;
    loader.setAttribute("aria-hidden", "true");
  }

  function ensureAllProductsLoaded(root) {
    if (root.getAttribute("data-all-products-loaded") === "true") {
      return Promise.resolve();
    }

    if (root._allProductsPromise) {
      return root._allProductsPromise;
    }

    var totalPages = parseInt(root.getAttribute("data-total-pages"), 10);
    var currentPage = parseInt(root.getAttribute("data-current-page"), 10) || 1;
    if (!totalPages || totalPages <= 1) {
      root.setAttribute("data-all-products-loaded", "true");
      return Promise.resolve();
    }

    var grid = root.querySelector("[data-filter-grid]");
    var rootId = root.id;
    var pageCards = [];
    pageCards[currentPage] = Array.prototype.slice.call(
      grid ? grid.querySelectorAll("[data-product-card]") : []
    );

    root.setAttribute("data-all-products-loading", "true");
    root._allProductsPromise = (async function () {
      var pages = [];
      for (var page = 1; page <= totalPages; page++) {
        if (page !== currentPage) pages.push(page);
      }

      var nextPageIndex = 0;
      async function loadNextPage() {
        while (nextPageIndex < pages.length) {
          var pageNumber = pages[nextPageIndex++];
          var response = await fetch(buildFetchUrl(pageNumber));
          if (!response.ok) continue;

          var html = await response.text();
          var doc = new DOMParser().parseFromString(html, "text/html");
          var remoteRoot =
            (rootId && doc.getElementById(rootId)) || doc.querySelector("[data-collection-filter]");
          if (!remoteRoot) continue;

          var remoteGrid = remoteRoot.querySelector("[data-filter-grid]");
          if (!remoteGrid) continue;
          pageCards[pageNumber] = Array.prototype.slice.call(
            remoteGrid.querySelectorAll("[data-product-card]")
          );
        }
      }

      await Promise.all([loadNextPage(), loadNextPage()]);

      if (grid) {
        var knownUrls = {};
        var orderedCards = [];
        for (var orderedPage = 1; orderedPage <= totalPages; orderedPage++) {
          (pageCards[orderedPage] || []).forEach(function (card) {
            var productUrl = card.getAttribute("data-product-url");
            if (!productUrl || knownUrls[productUrl]) return;
            knownUrls[productUrl] = true;
            orderedCards.push(card);
          });
        }
        grid.replaceChildren.apply(grid, orderedCards);
      }

      root.setAttribute("data-all-products-loaded", "true");
      root.removeAttribute("data-all-products-loading");
      delete root._allProductsPromise;
    })();

    return root._allProductsPromise;
  }

  function togglePaginationMode(root, useClientPagination) {
    var serverNav = root.querySelector("[data-server-pagination]");
    var clientNav = root.querySelector("[data-filter-pagination]");

    if (serverNav) serverNav.hidden = useClientPagination;
    if (clientNav && !useClientPagination) {
      clientNav.hidden = true;
      clientNav.innerHTML = "";
    }
  }

  function updateCount(root, count) {
    var el = root.querySelector("[data-filter-count]");
    if (!el) return;
    var template = el.getAttribute("data-count-template");
    if (template && template.indexOf("__COUNT__") !== -1) {
      el.textContent = template.replace("__COUNT__", String(count));
    } else {
      el.textContent = String(count);
    }
  }

  function toggleEmpty(root, visibleCount) {
    var grid = root.querySelector("[data-filter-grid]");
    var empty = root.querySelector("[data-filter-empty]");

    if (grid) grid.hidden = visibleCount === 0;
    if (empty) empty.hidden = visibleCount !== 0;
  }

  function syncCheckboxes(root, selected) {
    GROUPS.forEach(function (group) {
      var handles = selected[group];
      var inputs = root.querySelectorAll(
        'input[name="filter.' + group + '"], input[name="filter.p.' + group + '"]'
      );
      inputs.forEach(function (input) {
        input.checked = handles.indexOf(input.value) !== -1;
      });
    });
  }

  function labelForHandle(root, group, handle) {
    var input = root.querySelector(
      'input[name="filter.' + group + '"][value="' + cssEscape(handle) + '"], ' +
        'input[name="filter.p.' + group + '"][value="' + cssEscape(handle) + '"]'
    );
    if (input) {
      var label = input.parentElement.querySelector(".collection-main__filter-item-label");
      if (label) return label.textContent.trim();
    }
    return handle;
  }

  function cssEscape(value) {
    if (window.CSS && window.CSS.escape) return window.CSS.escape(value);
    return value.replace(/["\\]/g, "\\$&");
  }

  function removeValueUrl(group, handle) {
    var params = new URLSearchParams(window.location.search);
    ["filter." + group, "filter.p." + group].forEach(function (name) {
      var remaining = params.getAll(name).filter(function (value) {
        return value.trim() !== handle;
      });
      params.delete(name);
      remaining.forEach(function (value) {
        params.append(name, value);
      });
    });
    params.delete("page");
    var query = params.toString();
    return window.location.pathname + (query ? "?" + query : "");
  }

  function buildChips(root, selected) {
    var container = root.querySelector("[data-active-filters]");
    if (!container) return;

    container.querySelectorAll("[data-collection-chip]").forEach(function (chip) {
      chip.remove();
    });

    var reset = container.querySelector("[data-filter-reset]");
    var anyChip = false;

    GROUPS.forEach(function (group) {
      selected[group].forEach(function (handle) {
        anyChip = true;
        var chip = document.createElement("a");
        chip.className = "collection-main__active-filter";
        chip.setAttribute("data-collection-chip", "");
        chip.href = removeValueUrl(group, handle);
        chip.textContent = labelForHandle(root, group, handle);
        if (reset) {
          container.insertBefore(chip, reset);
        } else {
          container.appendChild(chip);
        }
      });
    });

    var hasNativeChips = container.querySelector(
      ".collection-main__active-filter:not([data-collection-chip])"
    );
    container.hidden = !(anyChip || hasNativeChips);
  }

  function syncSortForms(root, selected) {
    var forms = root.querySelectorAll("[data-sort-form]");
    forms.forEach(function (form) {
      form.querySelectorAll("[data-filter-sort-input]").forEach(function (input) {
        input.remove();
      });
      GROUPS.forEach(function (group) {
        selected[group].forEach(function (handle) {
          var input = document.createElement("input");
          input.type = "hidden";
          input.name = "filter." + group;
          input.value = handle;
          input.setAttribute("data-filter-sort-input", "");
          form.appendChild(input);
        });
      });
    });
  }

  function appendEllipsis(pages) {
    var el = document.createElement("span");
    el.className = "collection-main__pagination-link";
    el.setAttribute("aria-hidden", "true");
    el.textContent = "…";
    pages.appendChild(el);
  }

  function appendPageNumber(pages, page, currentPage) {
    var pageEl;
    if (page === currentPage) {
      pageEl = document.createElement("span");
      pageEl.className = "collection-main__pagination-link is-active";
      pageEl.textContent = String(page);
    } else {
      pageEl = document.createElement("a");
      pageEl.className = "collection-main__pagination-link";
      pageEl.href = buildPageUrl(page);
      pageEl.setAttribute("data-filter-page", String(page));
      pageEl.textContent = String(page);
    }
    pages.appendChild(pageEl);
  }

  function renderPageWindow(pages, totalPages, currentPage) {
    var windowRadius = window.matchMedia("(max-width: 479px)").matches ? 0 : 2;
    var startPage = Math.max(1, currentPage - windowRadius);
    var endPage = Math.min(totalPages, currentPage + windowRadius);

    if (startPage > 1) {
      appendPageNumber(pages, 1, currentPage);
      if (startPage > 2) appendEllipsis(pages);
    }

    for (var i = startPage; i <= endPage; i++) {
      appendPageNumber(pages, i, currentPage);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) appendEllipsis(pages);
      appendPageNumber(pages, totalPages, currentPage);
    }
  }

  function renderPagination(root, totalPages, currentPage) {
    var nav = root.querySelector("[data-filter-pagination]");
    if (!nav) return;

    if (totalPages <= 1) {
      nav.hidden = true;
      nav.innerHTML = "";
      return;
    }

    nav.hidden = false;
    nav.innerHTML = "";

    var prev = document.createElement(currentPage > 1 ? "a" : "span");
    prev.className =
      "collection-main__nav-button collection-main__nav-button--prev" +
      (currentPage <= 1 ? " is-disabled" : "");
    prev.setAttribute("aria-label", "Previous page");
    if (currentPage > 1) {
      prev.href = buildPageUrl(currentPage - 1);
      prev.setAttribute("data-filter-page", String(currentPage - 1));
    }
    prev.innerHTML = NAV_SVG;
    nav.appendChild(prev);

    var pages = document.createElement("div");
    pages.className = "collection-main__pagination-pages";

    renderPageWindow(pages, totalPages, currentPage);

    nav.appendChild(pages);

    var next = document.createElement(currentPage < totalPages ? "a" : "span");
    next.className =
      "collection-main__nav-button" + (currentPage >= totalPages ? " is-disabled" : "");
    next.setAttribute("aria-label", "Next page");
    if (currentPage < totalPages) {
      next.href = buildPageUrl(currentPage + 1);
      next.setAttribute("data-filter-page", String(currentPage + 1));
    }
    next.innerHTML = NAV_SVG;
    nav.appendChild(next);
  }

  function bindFilterForms(root) {
    if (root.getAttribute("data-filter-form-bound")) return;
    root.setAttribute("data-filter-form-bound", "true");

    root.querySelectorAll(".collection-main__filters-form").forEach(function (form) {
      form.addEventListener("submit", function () {
        showLoader(root);
      });
    });
  }

  function scrollToResults(root) {
    var target = root.querySelector("[data-collection-results]");
    if (!target) target = root.querySelector("[data-filter-grid]");
    if (!target) return;

    var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  }

  function bindPagination(root) {
    if (root.getAttribute("data-pagination-bound")) return;
    root.setAttribute("data-pagination-bound", "true");

    root.addEventListener("click", function (event) {
      var serverTrigger = event.target.closest("[data-server-pagination] a[href]");
      if (serverTrigger && root.contains(serverTrigger)) {
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        event.preventDefault();
        loadServerPage(root, serverTrigger.href, true);
        return;
      }

      var trigger = event.target.closest("[data-filter-page]");
      if (!trigger || !root.contains(trigger)) return;
      event.preventDefault();

      var page = parseInt(trigger.getAttribute("data-filter-page"), 10);
      if (!page) return;

      window.history.pushState({ filterPage: page }, "", buildPageUrl(page));
      apply(root);

      scrollToResults(root);
    });
  }

  async function loadServerPage(root, url, pushHistory) {
    if (root.getAttribute("data-page-loading") === "true") return;

    var grid = root.querySelector("[data-filter-grid]");
    root.setAttribute("data-page-loading", "true");
    if (grid) grid.setAttribute("aria-busy", "true");
    showLoader(root);

    try {
      var response = await fetch(url, {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!response.ok) throw new Error("Unable to load collection page");

      var html = await response.text();
      var doc = new DOMParser().parseFromString(html, "text/html");
      var remoteRoot =
        (root.id && doc.getElementById(root.id)) || doc.querySelector("[data-collection-filter]");
      var remoteGrid = remoteRoot && remoteRoot.querySelector("[data-filter-grid]");
      if (!remoteRoot || !remoteGrid || !grid) {
        throw new Error("Collection page results missing");
      }

      grid.replaceChildren.apply(
        grid,
        Array.prototype.slice.call(remoteGrid.children)
      );

      var currentServerNav = root.querySelector("[data-server-pagination]");
      var remoteServerNav = remoteRoot.querySelector("[data-server-pagination]");
      if (currentServerNav && remoteServerNav) {
        currentServerNav.replaceWith(remoteServerNav);
      } else if (currentServerNav) {
        currentServerNav.remove();
      } else if (remoteServerNav) {
        var results = root.querySelector("[data-filter-results]");
        var clientNav = root.querySelector("[data-filter-pagination]");
        if (results) results.insertBefore(remoteServerNav, clientNav || null);
      }

      ["data-total-items", "data-total-pages", "data-current-page"].forEach(function (name) {
        var value = remoteRoot.getAttribute(name);
        if (value !== null) root.setAttribute(name, value);
      });

      var count = parseInt(remoteRoot.getAttribute("data-total-items"), 10);
      if (!Number.isNaN(count)) updateCount(root, count);
      toggleEmpty(root, grid.children.length);

      var targetUrl = new URL(url, window.location.href);
      if (pushHistory) {
        window.history.pushState({ serverCollectionPage: true }, "", targetUrl.href);
      }

      window.requestAnimationFrame(function () {
        scrollToResults(root);
      });
    } catch (error) {
      window.location.assign(url);
    } finally {
      root.removeAttribute("data-page-loading");
      if (grid) grid.removeAttribute("aria-busy");
      hideLoader(root);
    }
  }

  function applyServerMode(root, selected) {
    togglePaginationMode(root, false);

    reorderCurrentGrid(root);

    root.querySelectorAll("[data-product-card]").forEach(function (card) {
      card.hidden = false;
    });

    var totalAttr = parseInt(root.getAttribute("data-total-items"), 10);
    var cards = root.querySelectorAll("[data-product-card]");
    var totalCount = totalAttr > 0 ? totalAttr : cards.length;

    updateCount(root, totalCount);
    toggleEmpty(root, totalCount);
    syncCheckboxes(root, selected);
    buildChips(root, selected);
    syncSortForms(root, selected);
  }

  function applyFilteredMode(root, selected) {
    togglePaginationMode(root, true);

    var cards = Array.prototype.slice.call(root.querySelectorAll("[data-product-card]"));
    var pageSize = getPageSize(root);
    var page = getPageFromUrl();

    var matching = cards.filter(function (card) {
      return cardMatches(card, selected);
    });
    matching = sortAvailableFirst(matching);

    var totalPages = Math.max(1, Math.ceil(matching.length / pageSize));
    if (page > totalPages) page = totalPages;

    cards.forEach(function (card) {
      card.hidden = true;
    });

    matching.slice((page - 1) * pageSize, page * pageSize).forEach(function (card) {
      card.hidden = false;
    });

    updateCount(root, matching.length);
    toggleEmpty(root, matching.length);
    renderPagination(root, totalPages, page);
    syncCheckboxes(root, selected);
    buildChips(root, selected);
    syncSortForms(root, selected);
  }

  async function applyAvailabilityMode(root, selected) {
    var pageSize = getPageSize(root);
    var page = getPageFromUrl();
    var grid = root.querySelector("[data-filter-grid]");
    if (!grid) return;

    togglePaginationMode(root, true);
    showLoader(root);

    try {
      var firstGroups = [
        await fetchAvailabilityPage(root, true, 1),
        await fetchAvailabilityPage(root, false, 1),
      ];
      var availablePages = { 1: firstGroups[0] };
      var unavailablePages = { 1: firstGroups[1] };
      var availableCount = firstGroups[0].total;
      var unavailableCount = firstGroups[1].total;
      var totalCount = availableCount + unavailableCount;
      var totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
      if (page > totalPages) page = totalPages;

      var start = (page - 1) * pageSize;
      var end = Math.min(start + pageSize, totalCount);
      var availableStart = Math.min(start, availableCount);
      var availableEnd = Math.min(end, availableCount);
      var unavailableStart = Math.max(0, start - availableCount);
      var unavailableEnd = Math.max(0, end - availableCount);

      var requests = [];
      pagesForRange(availableStart, availableEnd, pageSize).forEach(function (pageNumber) {
        if (availablePages[pageNumber]) return;
        requests.push(function () {
          return fetchAvailabilityPage(root, true, pageNumber).then(function (result) {
            availablePages[pageNumber] = result;
          });
        });
      });
      pagesForRange(unavailableStart, unavailableEnd, pageSize).forEach(function (pageNumber) {
        if (unavailablePages[pageNumber]) return;
        requests.push(function () {
          return fetchAvailabilityPage(root, false, pageNumber).then(function (result) {
            unavailablePages[pageNumber] = result;
          });
        });
      });
      for (var requestIndex = 0; requestIndex < requests.length; requestIndex++) {
        await requests[requestIndex]();
      }

      var visibleCards = [];
      for (var index = start; index < end; index++) {
        var card =
          index < availableCount
            ? cardAt(availablePages, index, pageSize)
            : cardAt(unavailablePages, index - availableCount, pageSize);
        if (card) visibleCards.push(card);
      }
      grid.replaceChildren.apply(grid, visibleCards);

      root.setAttribute("data-availability-ordering", "true");
      root.setAttribute("data-total-items", String(totalCount));
      updateCount(root, totalCount);
      toggleEmpty(root, visibleCards.length);
      renderPagination(root, totalPages, page);
      syncCheckboxes(root, selected);
      buildChips(root, selected);
      syncSortForms(root, selected);
    } catch (error) {
      applyServerMode(root, selected);
    } finally {
      hideLoader(root);
    }
  }

  function apply(root) {
    var selected = getSelected();
    var totalItems = parseInt(root.getAttribute("data-total-items"), 10) || 0;

    if (totalItems === 0) {
      hideLoader(root);
      applyServerMode(root, selected);
      return;
    }

    if (
      !hasSelection(selected) &&
      root.getAttribute("data-availability-ordering") !== "true" &&
      !hasUnavailableCards(root)
    ) {
      hideLoader(root);
      applyServerMode(root, selected);
      return;
    }

    if (!hasSelection(selected)) {
      applyAvailabilityMode(root, selected);
      return;
    }

    if (root.getAttribute("data-all-products-loaded") !== "true") {
      showLoader(root);
    }

    ensureAllProductsLoaded(root)
      .then(function () {
        applyFilteredMode(root, selected);
      })
      .finally(function () {
        hideLoader(root);
      });
  }

  function init() {
    var roots = document.querySelectorAll("[data-collection-filter]");
    roots.forEach(function (root) {
      bindPagination(root);
      bindFilterForms(root);
      if (hasSelection(getSelected())) {
        showLoader(root);
      }
      apply(root);
    });
  }

  window.addEventListener("popstate", function () {
    document.querySelectorAll("[data-collection-filter]").forEach(function (root) {
      var serverNav = root.querySelector("[data-server-pagination]");
      var clientNav = root.querySelector("[data-filter-pagination]");
      var usingClientPagination = clientNav && !clientNav.hidden;
      var currentPage = parseInt(root.getAttribute("data-current-page"), 10) || 1;
      var requestedPage = getPageFromUrl();

      if (serverNav && !usingClientPagination && currentPage !== requestedPage) {
        loadServerPage(root, window.location.href, false);
      } else {
        apply(root);
      }
    });
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
